<template>
  <v-container fluid>
    <v-dialog v-model="visibleDialog" persistent max-width="950">
      <v-card id="upload_document_card">
        <v-card-title>{{ $t('documents.content.uploadDocument') }}</v-card-title>
        <v-card-text class="card-content">
          <v-text-field
            id="upload_document_filename" v-model="fileName" :disabled="bulkUploadMode && !isAsicModeActive"
            :label="$t('main.name')"
          ></v-text-field>
          <drag-drop-upload
            id="upload_file" :key="dragDropKey" given-height="20vh" max-width="auto"
            :valid-formats="['application/pdf']" :current-file="uploadedFiles" :description="$t('dragDrop.uploadFiles')"
            @uploaded="handleDragDrop"
          >
            <v-progress-circular
              v-if="isUploading" slot="progress-bar" indeterminate color="primary"
              class="mb-4"
            ></v-progress-circular>
          </drag-drop-upload>

          <v-switch
            id="ordered_switch" v-model="chosenSignatureType" inset color="blue darken-2"
            :false-value="types.UNORDERED" :true-value="types.ORDERED" :label="chosenSignatureType === types.UNORDERED
              ? $t('documentSignatureCustomization.content.createChain.chainType.unorderedDescription')
              : $t('documentSignatureCustomization.content.createChain.chainType.orderedDescription')"
            prepend-icon="mdi-graph-outline" @click="_computedWatchers.canSign.run()"
          >
          </v-switch>

          <!-- Participants Mode Switch -->
          <v-switch
            id="participants_mode_switch" v-model="useIndividualParticipants"
            color="blue darken-2"
            inset
            :disabled="!bulkUploadMode || isAsicModeActive"
            :label="useIndividualParticipants
              ? $t('documentSignatureCustomization.content.createChain.participantsMode.individual')
              : $t('documentSignatureCustomization.content.createChain.participantsMode.global')"
            prepend-icon="mdi-account-group"
            @change="onParticipantsModeChange"
          ></v-switch>

          <!-- Document Tabs (Individual Mode) -->
          <div v-if="useIndividualParticipants && bulkUploadMode" class="mb-4 ml-2 mr-2">
            <v-tabs
              v-model="activeDocumentIndex" background-color="transparent" color="primary"
              show-arrows
              class="custom-tabs"
            >
              <v-tab
                v-for="(file, index) in uploadedFiles"
                :key="index"
                :value="index"
                class="pl-3 text-none"
              >
                <v-badge
                  :content="getDocumentParticipantCount(index)"
                  :color="isDocumentConfigured(index) ? 'success' : 'warning'"
                  overlap
                  offset-x="10"
                  offset-y="10"
                  inline
                >
                  {{ getShortFileName(file.name) }}
                </v-badge>
                <v-icon v-if="isDocumentConfigured(index)" color="success" small class="ml-2">
                  mdi-check-circle
                </v-icon>
              </v-tab>
            </v-tabs>

            <!-- Copy/Apply Buttons (Individual Mode) -->
            <v-row class="mt-3 mb-3" align="center">
              <v-col cols="auto">
                <v-btn
                  v-if="activeDocumentIndex > 0"
                  id="copy_from_previous"
                  :disabled="activeDocumentIndex === 0 || !hasPreviousDocumentParticipants"
                  color="primary"
                  outlined
                  small
                  @click="copyFromPrevious"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  {{ $t('documentSignatureCustomization.content.createChain.participants.copyFrom', { name: getPreviousDocumentName }) }}
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  id="apply_to_remaining"
                  :disabled="currentParticipants.length === 0 || activeDocumentIndex >= uploadedFiles.length - 1"

                  outlined
                  small
                  @click="applyToAllRemaining"
                >
                  <v-icon left small>mdi-content-duplicate</v-icon>
                  {{ $t('documentSignatureCustomization.content.createChain.participants.applyToAll') }}
                </v-btn>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-chip
                  :color="allDocumentsConfigured ? 'success' : 'warning'"
                  small
                  outlined
                >
                  <v-icon left small>
                    {{ allDocumentsConfigured ? 'mdi-check-all' : 'mdi-alert-circle' }}
                  </v-icon>
                  {{ $t('documentSignatureCustomization.content.createChain.participants.configureCount', { configuredDocumentsCount: configuredDocumentsCount, uploadedFilesCount: uploadedFiles.length}) }}
                </v-chip>
              </v-col>
            </v-row>
          </div>

          <!-- Participants Table -->
          <div v-if="useIndividualParticipants && bulkUploadMode" class="mb-2">
            <v-subheader class="pl-0">
              <v-icon left>mdi-account-multiple</v-icon>
              {{ $t('documentSignatureCustomization.content.createChain.participants.participantsFor', { name: getCurrentDocumentName }) }}
            </v-subheader>
          </div>

          <v-data-table
            id="virtual-scroll-table" ref="dtable" :headers="headers" :items="currentParticipants"
            item-key="uniqueId" :items-per-page="-1" :no-data-text="$t('dataTable.content.noDataAvailable')"
            hide-default-footer dense class="my-2"
          >
            <template #body="props">
              <draggable :list="currentParticipants" tag="tbody" @change="_computedWatchers.canSign.run()">
                <tr v-for="(user, index) in props.items" :key="index">
                  <td>
                    <span>{{ getRank(user) }}</span>
                  </td>
                  <td>
                    <long-text :text="getFullName(user)">
                    </long-text>
                  </td>
                  <td>
                    <span id="user_email" class="caption">
                      &lt;<long-text :text="user.email" :max-char="29">
                      </long-text>&gt;
                    </span>
                  </td>
                  <td>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          id="toggle_user_role" v-bind="attrs" icon
                          :disabled="!!userGroupsEnabled && isUserDisabledForRoleChange(user)" v-on="on"
                          @click="user.role = getToggledUserRole(user)"
                        >
                          <v-icon>
                            {{ getToggledUserRoleIcon(user.role) }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ getRoleTooltipText(user.role) }}</span>
                    </v-tooltip>
                  </td>
                  <td align="right">
                    <v-row>
                      <v-spacer></v-spacer>

                      <!-- Menu for setting Subsigner -->
                      <v-btn
                        v-show="user.role === memberRole.CLERK" id="set_subsigner_button" text class="pa-0"
                        @click="showSubsignerMenu(user, index, $event['clientX'], $event['clientY']) "
                      >
                        <v-icon>mdi-account-supervisor</v-icon>
                        {{ user.subsignerName ? $t('dataTable.content.action.changeSubsigner') :
                          $t('dataTable.content.action.setSubsigner') }}
                      </v-btn>

                      <v-btn text color="error" @click="removeUser(user, index)">
                        <v-icon>mdi-account-remove</v-icon>
                        {{ $t('dataTable.content.action.removeUser') }}
                      </v-btn>

                      <v-icon>mdi-drag-vertical</v-icon>
                    </v-row>
                  </td>
                </tr>
              </draggable>
            </template>
          </v-data-table>

          <v-autocomplete
            id="autocomplete_input" :key="autocompleteKey" v-model="nextUserToAdd" :items="enabledUsers"
            item-text="email" item-value="id" return-object
            :label="$t('documentSignatureCustomization.content.createChain.recipient') + `*`"
            :placeholder="$t('autocomplete.startTyping')" prepend-inner-icon="mdi-fountain-pen-tip"
            :no-data-text="noDatatext" clearable outlined hide-details dense :search-input="search"
            @update:search-input="listEnabledUsersWatcher" @keydown.enter="addUser" @click.prevent="addUser"
          >
            <template slot="item" slot-scope="{ item }">
              <v-row align-content="center" @mouseup="addUser(item)">
                <span class="body-2 py-4">{{ item.firstName }} {{ item.lastName }} <span
                  v-if="!!item.email"
                  class="caption"
                >&lt;{{ item.email }}&gt; </span></span>
                <v-spacer></v-spacer>
              </v-row>
            </template>
            <!-- Button to add user if no results found -->
            <template v-if="!userGroupsEnabled && !enabledUsers.length && displayInviteButton" #no-data>
              <v-row>
                <v-col class="ml-3">
                  <v-label class="ml-6">{{ noDatatext }}</v-label>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn id="invite_user" class="ml-3" color="primary" @click="addUserAsInvite()">
                    {{
                      $t('documentSignatureCustomization.content.createChain.iniviteUserButton') }}
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-autocomplete>
          <v-btn
            id="add_me" color="transparent" tile class="pl-0 pr-1 mt-2 rounded-l-xl modalButton" :ripple="false"
            :disabled="!currentUser.roles.includes(userRole.SIGNER)" @click="addMe()"
          >
            <v-icon large class="modalButton">
              mdi-plus-circle
            </v-icon>
            {{ $t('documentSignatureCustomization.content.createChain.addme') }}
          </v-btn>

          <v-divider class="my-5"></v-divider>
          <v-text-field
            id="comment_text_field"
            v-model="creatorComment"
            prepend-icon="mdi-comment modalButtonColor--text large"
            persistent-hint
            class="mb-3"
          >
            <template #label>
              <span class="text--secondary">{{ $t('documentSignatureCustomization.comments.label') }}</span>
            </template>
          </v-text-field>
          <div id="advanced-panel" class="mt-0">
            <div
              class="d-flex align-center py-3 px-2 cursor-pointer"
              role="button"
              :aria-expanded="advancedPanel.toString()"
              tabindex="0"
              @click="advancedPanel = !advancedPanel"
              @keydown.space.prevent="advancedPanel = !advancedPanel"
              @keydown.enter.prevent="advancedPanel = !advancedPanel"
            >
              <v-icon class="mr-3 chevron" :class="{ 'chevron--open': advancedPanel }">
                mdi-chevron-down
              </v-icon>
              <div class="header-text ">
                {{ $t('documentSignatureCustomization.content.createChain.advancedOptions') }}
              </div>
            </div>

            <v-slide-y-transition>
              <v-container v-show="advancedPanel" class="pt-4">
                <v-row>
                  <v-switch
                    v-if="signingTrailEnabled"
                    id="signingtrail_switch"
                    v-model="useSigningTrail"
                    :disabled="isAsicModeActive"
                    color="blue darken-2"
                    :label="useSigningTrail
                      ? $t('documentSignatureCustomization.content.createChain.useSigningTrail.true')
                      : $t('documentSignatureCustomization.content.createChain.useSigningTrail.false')"
                    prepend-icon="mdi-file-certificate-outline"
                    inset
                    class="mb-2"
                  />

                  <v-radio-group
                    v-if="signingTrailEnabled && useSigningTrail"
                    v-model="attachedOrDetachedSigningTrail"
                    :disabled="isAsicModeActive"
                    row
                    class="pl-4"
                  >
                    <v-radio
                      id="radio_attached" color="blue darken-2"
                      :label="$t('documentSignatureCustomization.content.createChain.useSigningTrail.signingTrailMode.ATTACHED')"
                      value="ATTACHED"
                    />
                    <v-radio
                      id="radio_detached" color="blue darken-2"
                      :label="$t('documentSignatureCustomization.content.createChain.useSigningTrail.signingTrailMode.DETACHED')"
                      value="DETACHED"
                    />
                  </v-radio-group>
                </v-row>

                <v-row>
                  <v-switch
                    id="setSignatureFields"
                    v-model="setSignaturesMode"
                    :disabled="isAsicModeActive "
                    color="blue darken-2"
                    prepend-icon="mdi-border-radius"
                    :label="$t('documentSignatureCustomization.content.createChain.setSignatureFieldButton')"
                    inset
                    class="mb-3"
                  />
                </v-row>
                <v-row>
                  <v-switch

                    v-if="bulkUploadMode"
                    id="bulk_switch"
                    v-model="asicMode"
                    color="blue darken-2"
                    inset
                    :label="isAsicModeActive
                      ? $t('documentSignatureCustomization.content.createChain.asicExplainer')
                      : $t('documentSignatureCustomization.content.createChain.nonAsicExplainer')"
                    prepend-icon="mdi-archive-arrow-down"
                    class="mb-3"
                  />
                </v-row>
                <v-row>
                  <v-btn
                    id="csvUploadButton"
                    :disabled="isAsicModeActive"
                    text
                    class="d-flex pl-0 mb-3 align-center font-weight-medium"
                    tile
                    @click="triggerFileSelect"
                  >
                    <!-- <v-img
                      v-if="csvImage"
                      :src="csvImage"
                      contain
                      max-width="19"
                      max-height="24"
                      class="mr-2"
                    /> -->
                    <v-icon class="pl-0 mr-2">
                      mdi-file-plus-outline
                    </v-icon>
                    <span class="text-body-2 black--text">{{ $t("documentSignatureCustomization.content.createChain.csv.uploadCsvForParticipants") }}</span>
                    <input
                      ref="csvInput"
                      type="file"
                      accept=".csv"
                      class="d-none"
                      @change="handleCsvUpload"
                    />
                  </v-btn>
                </v-row>

                <v-row align="center" class="mt-10 pl-0">
                  <!-- <v-spacer></v-spacer> -->

                  <v-simple-checkbox

                    id="remember_settings"
                    v-model="rememberSettings"
                    color="grey darken-2"
                    :label="$t('documentSignatureCustomization.content.createChain.rememberSettings')"
                  />
                  <span class="text-caption">
                    {{ $t('documentSignatureCustomization.content.createChain.rememberSettings') }}
                  </span>
                </v-row>
              </v-container>
            </v-slide-y-transition>
          </div>
        </v-card-text>
        <v-card-actions class="d-flex flex-row">
          <v-btn id="cancel" outlined class="my-4" color="primary" @click="backToParent(false)">
            <v-icon>mdi-close-circle-outline</v-icon>
            {{ $t('main.cancel') }}
          </v-btn>

          <!-- SIGN -->
          <v-btn
            v-if="canSign && !setSignaturesMode" id="confirm_sign" color="success" :disabled="checkSignButtonDisabled"
            :loading="isUploading" @click="uploadDocument(true)"
          >
            <template v-if="!isApproveMode">
              <v-icon>mdi-draw-pen</v-icon>
              {{ $t('main.sign') }}
            </template>
            <template v-else>
              <v-icon>mdi-check-decagram</v-icon>
              {{ $t('main.continue') }}
            </template>
          </v-btn>
          <v-btn
            v-else id="confirm_upload" color="primary" :loading="isUploading" :disabled="checkSignButtonDisabled"
            @click="uploadDocument(false)"
          >
            <v-icon>mdi-plus-circle-outline</v-icon>
            {{ $t('main.upload') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <pdf-password-modal
      ref="pdfPasswordModal" :visible.sync="isPasswordModalVisible" :pdf-filename="pdfFilename"
      @passwordSpecified="handlePasswordSpecified" @passwordCancelled="handlePasswordCancelled"
    >
    </pdf-password-modal>

    <!-- MENU -->
    <v-dialog v-model="subsignerNameMenuVisible" max-width="378">
      <v-card id="subsigner_card" class="pa-2">
        <v-card-subtitle class="subtitle-1">
          {{ $t('dataTable.content.action.setSubsignerDescription') }}
        </v-card-subtitle>
        <v-card-text>
          <v-form @submit.prevent="setSubsigner(currentEditedUserIndex)">
            <v-text-field
              id="subsigner_input" ref="subsignerInput" v-model="subsignerName" clearable
              dense hide-details :label="$t('dataTable.content.action.subsignerName')"
              @keyup.enter="setSubsigner(currentEditedUserIndex)"
            />
            <v-btn
              id="save_subsigner" class="mt-2" color="primary"
              @click="setSubsigner(currentEditedUserIndex)"
            >
              <v-icon>mdi-check-circle-outline</v-icon>
              {{ $t('main.save') }}
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="incorrectShowed" max-width="378">
      <v-card id="incorrectShowed" class="pa-10 ">
        <v-card-text style="text-align: center">
          <v-container>
            <v-row justify="center">
              <v-icon x-large class="display-3 mb-2" color="error">mdi-block-helper</v-icon>
            </v-row>
            <v-row justify="center">
              <div class="text-h6 error--text">
                {{ $t('toastMessage.incorrectPDFPasswordBlocking') }}
              </div>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn id="OK_btn" color="primary" @click="getPasswordAgain">
            {{ $t('main.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DragDropUpload from '@/components/DragDropUpload.vue'
import LongText from '@/components/LongText.vue'
import PDFPasswordModal from '@/components/PDFPasswordModal'
import { showToastError, showToastSuccess } from '@/event-bus.js'
import documents from '@/services/api/documents'
import rssp from '@/services/api/rsspUtil.js'
import seal from '@/services/api/seal.js'
import signatureChains from '@/services/api/signatureChains'
import users from '@/services/api/users'
import debounce from 'lodash.debounce'
import Draggable from 'vuedraggable'
import SignatureChainStatus from './SignatureChainStatus.js'
import SignatureChainType from './SignatureChainType'
import memberRole from './memberRole'
import userRole from './userRole'

import csvImage from '@/assets/icons/csv.svg'
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs'
import { parseCsvParticipants } from './csvParser'

export default {
  components: {
    Draggable,
    DragDropUpload,
    LongText,
    'pdf-password-modal': PDFPasswordModal
  },
  emits: ['uploaded'],
  data() {
    return {
      userRole,
      autocompleteKey: 1,
      visibleDialog: true,
      uploadedFiles: [],
      fileName: '',
      userGroupsEnabled: this.$store.state.remoteConfig ? this.$store.state.remoteConfig.userGroupsEnabled : false,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      signingTrailEnabled: this.$store.state.remoteConfig.signingTrailEnabled,
      useSigningTrail: true,
      attachedOrDetachedSigningTrail: 'ATTACHED',
      displayInviteButton: false,
      invitableUserEmail: '',
      noCentralKeyWarningVisible: false,
      dragDropKey: 0,
      enabledUsers: [],
      addedUsers: [], // Global participants list
      documentParticipants: [], // Individual participants per document { documentIndex: participants[] }
      documentParticipantsErrors: [],
      useIndividualParticipants: false, // Toggle between global and individual modes
      activeDocumentIndex: 0, // Active document tab in individual mode
      csvFile: null,
      types: { ...SignatureChainType }, // only needed for template
      chosenSignatureType: SignatureChainType.UNORDERED,
      subsignerName: '',
      headers: [
        {
          text: this.$t('dataTable.header.users.rank'),
          sortable: false,
          align: 'left',
          value: 'rank'

        },
        {
          text: this.$t('dataTable.header.users.name'),
          align: 'left',
          value: 'name'
        },
        {
          text: this.$t('dataTable.header.users.email'),
          align: 'left',
          value: 'email'
        },
        {
          text: this.$t('documentSignatureCustomization.content.createChain.role.header'),
          align: 'left',
          value: 'role'

        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          width: '33%',

          sortable: false
        }
      ],
      rules: {
        required: v => !!v || this.$t('validation.form.requiredField')
      },

      search: '',
      currentEditedUserIndex: null,
      nextUserToAdd: {},
      includesHimself: false,
      // canSign: false,
      // progress bar
      isUploading: false,
      noDatatext: this.$t('autocomplete.startTyping'),
      memberRole: memberRole,
      asicMode: false,
      enabledUsersFixed: [],
      subsignerNameMenuVisible: false,
      menuX: 0,
      menuY: 0,
      incorrectShowed: false,
      pdfFilename: null,
      pdf_password: null,
      isPasswordModalVisible: false,
      passwordModalResolve: null,
      passwordModalReject: null,
      csvImage: csvImage,
      advancedPanel: false,
      rememberSettings: false,
      // Comments
      creatorComment: '',
      setSignaturesMode: false
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },
    checkSignButtonDisabled() {
      if (this.useIndividualParticipants && this.bulkUploadMode) {
        return (this.uploadedFiles.length === 0) || !this.allDocumentsConfigured
      }
      const d = (this.uploadedFiles.length === 0) || this.currentParticipants.length === 0 || this.currentParticipants.every(x => x.role === memberRole.VIEWER)
      return d
    },
    bulkUploadMode() {
      return this.uploadedFiles.length > 1
    },
    isAsicModeActive() {
      return this.asicMode && this.bulkUploadMode
    },
    canSign() {
      const hasAddedUsers = this.currentParticipants.length > 0
      const isNotBulkUpload = !this.bulkUploadMode
      const isCurrentUserFirst =
        hasAddedUsers &&
        this.currentParticipants[0].id === this.$store.state.currentUser.id
      const isUnorderedWithHimself =
        this.chosenSignatureType === SignatureChainType.UNORDERED && this.includesHimself
      const res = hasAddedUsers && isNotBulkUpload && (isCurrentUserFirst || isUnorderedWithHimself)
      return res
    },
    isApproveMode() {
      const isCurrentUserFirstApprover =
        (this.currentParticipants[0]?.role === memberRole.APPROVER) && this.chosenSignatureType === SignatureChainType.ORDERED

      const isUnorderedCurrentUserApprover =
        this.chosenSignatureType === SignatureChainType.UNORDERED &&
        this.includesHimself &&
        this.currentParticipants.some(user => user.id === this.currentUser.id && user.role === memberRole.APPROVER)

      return isCurrentUserFirstApprover || isUnorderedCurrentUserApprover
    },

    isClerkMode() {
      const isCurrentUserFirstClerk =
        (this.currentParticipants[0]?.role === memberRole.CLERK) && this.chosenSignatureType === SignatureChainType.ORDERED

      const isUnorderedCurrentUserClerk =
        this.chosenSignatureType === SignatureChainType.UNORDERED &&
        this.includesHimself &&
        this.currentParticipants.some(user => user.id === this.currentUser.id && user.role === memberRole.CLERK)

      return isCurrentUserFirstClerk || isUnorderedCurrentUserClerk
    },

    sealEnabled() {
      return this.sealStatus.enabled
    },
    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },
    clerkEnabled() {
      // return true
      return this.$store.state.remoteConfig.clerkEnabled
    },

    currentParticipants() {
      if (this.useIndividualParticipants && this.bulkUploadMode) {
        return this.documentParticipants[this.activeDocumentIndex] || []
      }
      return this.addedUsers
    },

    allDocumentsConfigured() {
      if (!this.useIndividualParticipants || !this.bulkUploadMode) {
        return true
      }

      for (let i = 0; i < this.uploadedFiles.length; i++) {
        if (!this.isDocumentConfigured(i)) {
          return false
        }
      }
      return true
    },

    configuredDocumentsCount() {
      if (!this.useIndividualParticipants || !this.bulkUploadMode) {
        return this.uploadedFiles.length
      }

      let count = 0
      for (let i = 0; i < this.uploadedFiles.length; i++) {
        if (this.isDocumentConfigured(i)) {
          count++
        }
      }
      return count
    },

    hasPreviousDocumentParticipants() {
      if (this.activeDocumentIndex === 0) return false
      const prevParticipants = this.documentParticipants[this.activeDocumentIndex - 1]
      return prevParticipants && prevParticipants.length > 0
    },

    getPreviousDocumentName() {
      if (this.activeDocumentIndex === 0) return ''
      return this.getShortFileName(this.uploadedFiles[this.activeDocumentIndex - 1]?.name || '')
    },

    getCurrentDocumentName() {
      return this.uploadedFiles[this.activeDocumentIndex]?.name || ''
    }
  },

  async mounted() {
    this.dragDropKey -= 1
    await this.listEnabledUsers()

    const pageReq = {
      maxResults: 100
    }
    const resp = await users.listEnabledUsers('', pageReq)
    this.enabledUsersFixed = resp.items

    this.sealStatus = await seal.getSealStatus()

    this.useSigningTrail = false
    if (this.signingTrailEnabled) {
      if (this.sealActivated) {
        this.useSigningTrail = true
      }
    }

    await this.checkCredentials()

    this.loadSettings()
  },
  methods: {
    handleDragDrop(e) {
      // console.log('handleDragDrop', e)
      if (e === null) e = []
      this.uploadedFiles = e

      // Reset individual participants when files change
      if (this.useIndividualParticipants) {
        this.documentParticipants = {}
        this.documentParticipantsErrors = {}
        this.activeDocumentIndex = 0
        this.useIndividualParticipants = false
      }
    },

    onParticipantsModeChange() {
      if (this.useIndividualParticipants && this.bulkUploadMode) {
        // Switching to individual mode - copy global list to first document
        if (this.addedUsers.length > 0) {
          this.$set(this.documentParticipants, 0, [...this.addedUsers])
        }
        this.activeDocumentIndex = 0
      } else {
        // Switching to global mode - keep current global list
        this.documentParticipants = {}
      }
    },

    isDocumentConfigured(documentIndex) {
      const participants = this.documentParticipants[documentIndex] || []
      return participants.length > 0 && participants.some(p => p.role !== memberRole.VIEWER)
    },

    getDocumentParticipantCount(documentIndex) {
      const participants = this.documentParticipants[documentIndex] || []
      return participants.length
    },

    getShortFileName(fileName) {
      if (fileName.length <= 20) return fileName
      const extension = fileName.split('.').pop()
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'))
      return nameWithoutExt.substring(0, 15) + '...' + extension
    },

    copyFromPrevious() {
      if (this.activeDocumentIndex === 0 || !this.hasPreviousDocumentParticipants) return

      const previousParticipants = this.documentParticipants[this.activeDocumentIndex - 1]
      const copiedParticipants = previousParticipants.map(user => ({
        ...user,
        uniqueId: Date.now() + Math.random() // Generate new unique ID
      }))

      this.$set(this.documentParticipants, this.activeDocumentIndex, copiedParticipants)
      this.updateIncludesHimself()
    },

    applyToAllRemaining() {
      if (this.currentParticipants.length === 0) return

      for (let i = this.activeDocumentIndex + 1; i < this.uploadedFiles.length; i++) {
        const copiedParticipants = this.currentParticipants.map(user => ({
          ...user,
          uniqueId: Date.now() + Math.random() + i // Generate new unique ID
        }))
        this.$set(this.documentParticipants, i, copiedParticipants)
      }
    },

    updateIncludesHimself() {
      this.includesHimself = this.currentParticipants.some(user => user.id === this.currentUser.id)
    },

    async getPdfInfo(pdfDocument) {
      const metadata = await pdfDocument.getMetadata()
      const hasDigitalSignature = metadata.info.IsSignaturesPresent ? metadata.info.IsSignaturesPresent : false

      const isEncrypted = metadata.info.EncryptFilterName !== null
      const encryptionType = metadata.info.EncryptFilterName

      return {
        isEncrypted,
        encryptionType,
        hasDigitalSignature
      }
    },
    base64ToUint8Array(base64) {
      var raw = window.atob(base64)
      var uint8Array = new Uint8Array(raw.length)
      for (var i = 0; i < raw.length; i++) {
        uint8Array[i] = raw.charCodeAt(i)
      }
      return uint8Array
    },
    async uploadDocument(sign = false) {
      this.saveSettings()

      if (this.setSignaturesMode && !this.isAsicModeActive && !(this.currentParticipants.every(usr => usr.role === memberRole.APPROVER))) {
        await this.navToSignatueField()
        return
      }

      try {
        this.isUploading = true

        if (this.useIndividualParticipants && this.bulkUploadMode) {
          // Individual mode - upload each document with its own participants
          await this.uploadDocumentsIndividually(sign)
        } else {
          // Global mode - original behavior
          await this.uploadDocumentsGlobally(sign)
        }
      } catch (error) {
        console.error('error at uploadDocument', error)
        error.fileName = ''
        error.fileName = this.fileName
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
        this.isUploading = false
      }
    },

    async handleSigningRedirect(chainId, documentId, signatures) {
      const mockChainResp = {
        id: chainId,
        origDocument: { id: documentId },
        signatures: signatures,
        type: this.chosenSignatureType
      }

      if (this.canSign && this.isClerkMode) {
        this.$store.commit('setCurrentChain', mockChainResp)
        this.$router.push('/document/' + documentId + '/clerk/' + this.getUserSignatureIdForClerk(mockChainResp) + '/' + this.getCurrentSignatureId(mockChainResp))
      } else if (this.canSign && this.isApproveMode) {
        this.$router.push('/document/' + documentId + '/approve-customization/' + this.getUserSignatureIdForApprover(mockChainResp) + '/' + this.getCurrentSignatureId(mockChainResp))
      } else {
        this.$router.push('/document/' + chainId + '/' + documentId + '/customization/signature/' + this.getUserSignatureId(mockChainResp) + this.getCurrentSignatureId(mockChainResp))
      }
    },

    async uploadDocumentsIndividually(sign = false) {
      const results = []
      let hasErrors = false

      for (let i = 0; i < this.uploadedFiles.length; i++) {
        const file = this.uploadedFiles[i]
        const participants = this.documentParticipants[i] || []

        if (participants.length === 0 || participants.every(x => x.role === memberRole.VIEWER)) {
          showToastError(`Document ${file.name} has no valid participants`)
          hasErrors = true
          continue
        }

        try {
          // Process PDF (password check, etc.)
          const pdfProcessed = await this.processPdfFile(file)
          if (!pdfProcessed) {
            hasErrors = true
            continue
          }

          // Upload document
          const fileName = this.fileName && i === 0 ? this.fileName : file.name
          const uploadResp = await documents.uploadDocument(fileName, file)

          // Create signature chain
          const chainResp = await this.createSignatureChainForDocument(uploadResp, participants)

          results.push({
            documentId: chainResp.origDocument.id,
            chainId: chainResp.id,
            fileName: fileName,
            signatures: chainResp.signatures
          })
        } catch (error) {
          const errorMsg = this.getErrorMessage(error)
          showToastError(`Error processing ${file.name}: ${errorMsg}`)
          hasErrors = true
        }
      }

      this.isUploading = false

      if (!hasErrors && results.length > 0) {
        if (sign && results.length === 1) {
          // Handle signing for single document
          const result = results[0]
          await this.handleSigningRedirect(result.chainId, result.documentId, result.signatures)
        } else {
          showToastSuccess(this.$t('toastMessage.succesfullyCreated'))
          this.$store.commit('removeCurrentFiles')
          this.$emit('uploaded')
          this.backToParent()
        }
      }
    },

    async uploadDocumentsGlobally(sign = false) {
      if (!this.fileName) {
        this.fileName = this.uploadedFiles[0].name
      }

      const processFiles = async () => {
        for (const file of this.uploadedFiles) {
          const processed = await this.processPdfFile(file)
          if (!processed) {
            return false
          }
        }
        return true
      }

      // Call the function to start processing all uploaded files
      const filesAreOK = await processFiles()
      if (!filesAreOK) {
        this.isUploading = false
        return
      }

      let uploadResp

      try {
        if (this.bulkUploadMode) {
          uploadResp = await documents.uploadBatch(this.isAsicModeActive ? this.fileName : undefined, this.uploadedFiles)
        } else {
          uploadResp = await documents.uploadDocument(this.fileName, this.uploadedFiles[0])
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
        this.isUploading = false
        return
      }

      if (uploadResp === undefined) {
        this.isUploading = false
        return
      }

      const chainResp = await this.createSignatureChainForDocument(uploadResp, this.addedUsers, true)

      if (chainResp) {
        if (sign && !this.bulkUploadMode) {
          await this.handleSigningRedirect(chainResp.id, chainResp.origDocument.id, chainResp.signatures)
        } else {
          showToastSuccess(this.$t('toastMessage.succesfullyCreated'))
          this.$store.commit('removeCurrentFiles')
          this.$emit('uploaded')
          this.backToParent()
        }
      }

      this.isUploading = false
    },

    async processPdfFile(file) {
      try {
        const arrayBuffer = await this.readFileAsArrayBuffer(file)
        const pdfData = this.arrayBufferToBase64(arrayBuffer)
        let pdfDocument

        const attachedSigningTrailMode = this.signingTrailEnabled && this.useSigningTrail && !this.isAsicModeActive && this.attachedOrDetachedSigningTrail === 'ATTACHED'

        try {
          pdfDocument = await pdfjs.getDocument({ data: atob(pdfData) }).promise
          const { hasDigitalSignature } = await this.getPdfInfo(pdfDocument)

          if (hasDigitalSignature && attachedSigningTrailMode) {
            showToastError(this.$t('toastMessage.cantSignAttachedSigningTrailWhenPDFAlreadySigned', { filename: file.name }))
            return false
          }
        } catch (e) {
          if (e.name === 'PasswordException') {
            const passwordSpecified = await this.getPDFPassword(file.name)

            if (passwordSpecified === null) {
              console.error('Password dialog cancelled for file:', file.name, e)
              return false
            }

            const pdfPasswordModalComponent = this.$refs.pdfPasswordModal
            pdfPasswordModalComponent.resetPassword()

            try {
              pdfDocument = await pdfjs.getDocument({
                data: atob(pdfData),
                password: passwordSpecified
              }).promise

              const { hasDigitalSignature } = await this.getPdfInfo(pdfDocument)
              if (hasDigitalSignature && attachedSigningTrailMode) {
                showToastError(this.$t('toastMessage.cantSignAttachedSigningTrailWhenPDFAlreadySigned', { filename: file.name }))
                return false
              }
            } catch (e) {
              if (e.name === 'PasswordException') {
                showToastError(this.$t('toastMessage.incorrectPDFPasswordBlocking'))
                return false
              }
            }
          } else {
            throw e
          }
        }
        return true
      } catch (error) {
        console.error('Error processing file:', file.name, error)
        return false
      }
    },

    async createSignatureChainForDocument(uploadResp, participants, isBulkOrBatch = false) {
      const viewerEmails = participants.filter(user => user.role === memberRole.VIEWER || user.role === memberRole.CREATOR).map(x => x.email)

      const createSignatureChainRequest = {
        type: this.chosenSignatureType,
        signers: [],
        viewerEmails: viewerEmails,
        comment: this.creatorComment.length > 0 ? this.creatorComment : null
      }

      participants.forEach(user => {
        if (user.role === memberRole.VIEWER) {
          return
        }
        const addableSignature = {
          email: user.email,
          clerkMode: this.clerkEnabled && (user.role === memberRole.CLERK),
          approver: user.role === memberRole.APPROVER
        }
        if (this.clerkEnabled && (user.role === memberRole.CLERK)) {
          addableSignature.subsignerName = user.subsignerName
        }

        createSignatureChainRequest.signers.push(addableSignature)
      })

      if (isBulkOrBatch && this.bulkUploadMode) {
        createSignatureChainRequest.batchId = uploadResp.id
      } else {
        createSignatureChainRequest.origDocId = uploadResp.id
      }

      if (this.signingTrailEnabled && !this.isAsicModeActiveasicMode) {
        createSignatureChainRequest.signingTrailMode = this.useSigningTrail ? this.attachedOrDetachedSigningTrail : 'NONE'
      }

      try {
        let resp
        if (isBulkOrBatch && (this.isAsicModeActive || !this.bulkUploadMode)) {
          // single chain or asic
          resp = await signatureChains.createSignatureChain(createSignatureChainRequest)
        } else if (isBulkOrBatch && this.bulkUploadMode) {
          // parallel chains
          resp = await signatureChains.createMultiSignatureChain(createSignatureChainRequest)
        } else {
          // individual document
          resp = await signatureChains.createSignatureChain(createSignatureChainRequest)
        }
        return resp
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
        throw error
      }
    },

    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        fileReader.onload = (e) => resolve(e.target.result)
        fileReader.onerror = (e) => reject(e)
        fileReader.readAsArrayBuffer(file)
      })
    },

    arrayBufferToBase64(arrayBuffer) {
      let binary = ''
      const bytes = new Uint8Array(arrayBuffer)
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return btoa(binary)
    },

    listEnabledUsersWatcher: debounce(function(search) {
      this.listEnabledUsers(search)
    }, 500),

    isValidEmail(email) {
      // Regular expression for validating an email address
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    async listEnabledUsers(searchUser) {
      const pageReq = {
        maxResults: 5
      }
      let searchUserlen = 0
      try {
        searchUserlen = searchUser.length
      } catch (err) {

      }
      if (searchUserlen > 0) {
        try {
          const resp = await users.listEnabledUsers(searchUser, pageReq)
          this.enabledUsers = resp.items

          if (resp.items.length === 0) {
            this.noDatatext = this.$t('documentSignatureCustomization.content.createChain.userNotFound')

            if (this.isValidEmail(searchUser)) {
              this.invitableUserEmail = searchUser
              this.displayInviteButton = true
            } else {
              this.displayInviteButton = false
            }
          } else {
            this.displayInviteButton = false
          }
        } catch (error) {
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        }
      } else {
        this.noDatatext = this.$t('autocomplete.startTyping')
      }
    },

    removeUser(user, index) {
      if (user.id === this.currentUser.id) {
        this.includesHimself = false
      }

      if (this.useIndividualParticipants && this.bulkUploadMode) {
        // Remove from current document's participants
        const currentParticipants = [...this.currentParticipants]
        currentParticipants.splice(index, 1)
        this.$set(this.documentParticipants, this.activeDocumentIndex, currentParticipants)
      } else {
        // Remove from global participants
        this.addedUsers.splice(index, 1)
      }

      this.updateIncludesHimself()
    },

    addUser(user) {
      if (user.id === this.currentUser.id) {
        if (this.currentUser.roles.includes(userRole.SIGNER)) {
          user.role = memberRole.CREATOR_AND_SIGNER
        } else {
          showToastError(this.$t('documentSignatureCustomization.warning.creatorCantAddAsViewer'))
          return
        }
      } else {
        if (user.roles && user.roles.includes(userRole.SIGNER)) {
          user.role = memberRole.SIGNER
        } else {
          user.role = memberRole.VIEWER
        }
      }

      this.nextUserToAdd = user

      // we need to copy the object and assign a unique id for it to avoid Vue duplicate key warnings
      this.nextUserToAdd = Object.assign({}, this.nextUserToAdd)
      this.nextUserToAdd.uniqueId = Date.now() + Math.random()

      // explanation: somehow a empty array is not an array: error: .include is  not a function
      if ((this.nextUserToAdd !== undefined) && (this.nextUserToAdd.email)) {
        if (this.useIndividualParticipants && this.bulkUploadMode) {
          // Add to current document's participants
          const currentParticipants = [...this.currentParticipants]
          currentParticipants.push(this.nextUserToAdd)
          this.$set(this.documentParticipants, this.activeDocumentIndex, currentParticipants)
        } else {
          // Add to global participants
          this.addedUsers.push(this.nextUserToAdd)
        }

        if (this.nextUserToAdd.id === this.currentUser.id) {
          this.includesHimself = true
        }
        this.nextUserToAdd = ''

        this.autocompleteKey++ // hack: this needs to Vue clear the autocomplete field correctly

        this.search = ''
        this.displayInviteButton = false
        this.updateIncludesHimself()
      }
      const container = this.$refs.dtable.$el
      container.scrollTop = container.scrollHeight - 1
    },
    addMe() {
      this.addUser(this.currentUser)
    },
    setSubsigner(index) {
      if (this.useIndividualParticipants && this.bulkUploadMode) {
        const currentParticipants = [...this.currentParticipants]
        this.$set(currentParticipants[index], 'subsignerName', this.subsignerName)
        this.$set(this.documentParticipants, this.activeDocumentIndex, currentParticipants)
      } else {
        this.$set(this.addedUsers[index], 'subsignerName', this.subsignerName)
      }
      this.subsignerNameMenuVisible = false // Close the menu
    },

    addUserAsInvite() {
      const invitableUser = {}
      invitableUser.roles = []
      invitableUser.roles.push(userRole.SIGNER)
      invitableUser.email = this.invitableUserEmail
      this.addUser(invitableUser)
    },

    backToParent(e) {
      if (!e) {
        this.$store.commit('removeCurrentFiles')
        this.$router.push({ name: 'SignatureChains' })
      }
    },

    getUserSignatureId(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED))
      return userSignature.id
    },

    getUserSignatureIdForApprover(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.approver === true))
      return userSignature.id
    },

    getUserSignatureIdForClerk(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.clerkMode === true))
      return userSignature.id
    },

    getCurrentSignatureId(item) {
      let currentSignatureId = '/'
      if (item.type === SignatureChainType.ORDERED) {
        currentSignatureId = ''
      } else {
        if (item.currentSignatureId === undefined) {
          currentSignatureId = ''
        } else {
          currentSignatureId += item.currentSignatureId
        }
        // currentSignatureId += item.signatures.findIndex(signature => signature.user.id === this.currentUser.id)
      }

      return currentSignatureId
    },

    getToggledUserRole(user) {
      const { role } = user

      const userHaveRoles = user.roles
      const userHaveApprover = userHaveRoles && user.roles.includes(userRole.APPROVER)
      const userHaveSign = userHaveRoles && user.roles.includes(userRole.SIGNER)
      const canAddApprover = userHaveApprover
      const canAddSigner = !userHaveRoles || userHaveSign
      const canAddViewer = userHaveRoles
      const canAddClerk = this.clerkEnabled && (!userHaveRoles || userHaveSign)
      if (user.id === this.currentUser.id) {
        switch (role) {
          case memberRole.CREATOR_AND_SIGNER:
            return this.clerkEnabled ? memberRole.CLERK : (canAddApprover ? memberRole.APPROVER : memberRole.VIEWER)
          case memberRole.CLERK:
            return this.clerkEnabled ? (canAddApprover ? memberRole.APPROVER : memberRole.VIEWER) : memberRole.VIEWER
          case memberRole.VIEWER:
            return canAddApprover ? memberRole.APPROVER : memberRole.CREATOR_AND_SIGNER
          case memberRole.APPROVER:
            return memberRole.CREATOR_AND_SIGNER
          default:
            return role
        }
      } else {
        const rolesOrder = [
          this.memberRole.SIGNER,
          this.memberRole.CLERK,
          this.memberRole.VIEWER,
          this.memberRole.APPROVER
        ]
        const canAddMap = {
          [this.memberRole.SIGNER]: canAddSigner,
          [this.memberRole.CLERK]: canAddClerk,
          [this.memberRole.VIEWER]: canAddViewer,
          [this.memberRole.APPROVER]: canAddApprover
        }

        const currentIndex = rolesOrder.indexOf(role)
        const totalRoles = rolesOrder.length

        for (let i = 1; i <= totalRoles; i++) {
          const nextIndex = (currentIndex + i) % totalRoles
          const nextRole = rolesOrder[nextIndex]

          if (canAddMap[nextRole]) {
            return nextRole
          }
        }
        console.error('Unknown role for non-current user', role)
        return role
      }
    },
    getToggledUserRoleIcon(role) {
      switch (role) {
        case memberRole.CREATOR:
          return 'mdi-file-sign'
        case memberRole.SIGNER:
          return 'mdi-file-sign'
        case memberRole.CREATOR_AND_SIGNER:
          return 'mdi-file-sign'
        case memberRole.VIEWER:
          return 'mdi-eye-outline'
        case memberRole.CLERK:
          return 'mdi-account-tie'
        case memberRole.APPROVER:
          return 'mdi-check-decagram'
        default:
          console.error('Unknown role', role)
      }
    },
    getRoleTooltipText(role) {
      switch (role) {
        case memberRole.CREATOR_AND_SIGNER:
          return this.$t('documentSignatureCustomization.content.createChain.role.creatorExplainer')
        case memberRole.SIGNER:
          return this.$t('documentSignatureCustomization.content.createChain.role.signerExplainer')
        case memberRole.VIEWER:
          return this.$t('documentSignatureCustomization.content.createChain.role.viewerExplainer')
        case memberRole.CLERK:
          return this.$t('documentSignatureCustomization.content.createChain.role.clerkExplainer')
        case memberRole.APPROVER:
          return this.$t('signatureChains.states.approverExplainer')
        default:
          console.error('Unknown role', role)
          break
      }
    },
    getRank(user) {
      if (this.chosenSignatureType === SignatureChainType.UNORDERED || user.role === memberRole.VIEWER) return '•'
      else {
        const signers = this.currentParticipants.filter(x => x.role !== memberRole.VIEWER)
        const index = signers.findIndex(x => x.uniqueId === user.uniqueId)

        return index + 1
      }
    },
    getFullName(user) {
      if (user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`
      } else
      if (user.firstName) {
        return `${user.firstName}`
      } else
      if (user.lastName) {
        return `${user.lastName}`
      } else return ''
    },
    async checkCredentials() {
      try {
        const resp = await rssp.listMyActivatedCredentials()
        resp.content = resp.content.filter(elem => ((elem.keyPurpose === 'ASYMMETRIC_SIGN') && (elem.certChain.length > 0)))

        const activatedCredentials = resp.content

        if (this.sealEnabled && !this.sealActivated && (activatedCredentials && activatedCredentials.length === 0)) {
          this.noCentralKeyWarningVisible = true
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    isUserDisabledForRoleChange(user) {
      const isEmailInArray = this.enabledUsersFixed.some(item => item.email === user.email)
      return !isEmailInArray || (user.roles && !user.roles.includes(userRole.SIGNER))
    },

    showPasswordModal() {
      this.isPasswordModalVisible = true
      return new Promise((resolve, reject) => {
        this.passwordModalResolve = resolve
        this.passwordModalReject = reject
      })
    },

    async getPDFPassword(pdfFilename) {
      try {
        this.pdfFilename = pdfFilename
        const givenPassword = await this.showPasswordModal()
        return givenPassword
      } catch (error) {
        return null
      }
    },
    handlePasswordSpecified(password) {
      this.isPasswordModalVisible = false
      if (this.passwordModalResolve) {
        this.passwordModalResolve(password)
      }
    },
    handlePasswordCancelled() {
      this.isPasswordModalVisible = false
      if (this.passwordModalReject) {
        this.passwordModalReject()
      }
    },
    async getPasswordAgain() {
      this.incorrectShowed = false
      // await this.showPasswordModal()
    },
    showSubsignerMenu(item, index, x, y) {
      if (item.subsignerName) { this.subsignerName = item.subsignerName }

      this.currentEditedUserIndex = index
      this.menuX = x
      this.menuY = y
      this.subsignerNameMenuVisible = true
    },
    async navToSignatueField() {
      this.isUploading = true
      try {
        const isMultiDocument = this.bulkUploadMode && this.uploadedFiles.length > 1

        if (!isMultiDocument) {
          this.$store.commit('removeCurrentChainBatch')
          const uploadResp = await documents.uploadDocument(this.fileName || this.uploadedFiles[0].name, this.uploadedFiles[0])

          const createSignatureChainRequest = {
            type: this.chosenSignatureType,
            comment: this.creatorComment,
            fileName: this.fileName || this.uploadedFiles[0].name,
            addedUsers: this.currentParticipants,
            useSigningTrail: this.useSigningTrail,
            attachedOrDetachedSigningTrail: this.attachedOrDetachedSigningTrail
          }

          this.$store.commit('setCurrentChain', createSignatureChainRequest)
          this.$router.push(`/document/${uploadResp.id}/create/`)
          return
        }

        const items = []
        for (let i = 0; i < this.uploadedFiles.length; i++) {
          const file = this.uploadedFiles[i]
          const participants = this.useIndividualParticipants
            ? (this.documentParticipants[i] || [])
            : this.addedUsers
          const participantsCopy = participants.map(user => ({ ...user }))

          if (participantsCopy.length === 0 || participantsCopy.every(x => x.role === memberRole.VIEWER)) {
            showToastError(`Document ${file.name} has no valid participants`)
            return
          }

          const processed = await this.processPdfFile(file)
          if (!processed) {
            return
          }

          const fileName = this.fileName && i === 0 ? this.fileName : file.name
          const uploadResp = await documents.uploadDocument(fileName, file)

          items.push({
            docId: uploadResp.id,
            fileName: fileName,
            addedUsers: participantsCopy,
            type: this.chosenSignatureType,
            comment: this.creatorComment,
            useSigningTrail: this.useSigningTrail,
            attachedOrDetachedSigningTrail: this.attachedOrDetachedSigningTrail
          })
        }

        this.$store.commit('setCurrentChainBatch', { items, index: 0 })
        this.$store.commit('setCurrentChain', items[0])
        this.$router.push(`/document/${items[0].docId}/create/`)
      } finally {
        this.isUploading = false
      }
    },
    triggerFileSelect() {
      this.$refs.csvInput.click()
    },
    async handleCsvUpload(event) {
      const file = event.target.files[0]
      event.target.value = null

      if (!file) return

      try {
        const text = await file.text()
        const result = parseCsvParticipants(text)
        const missingFiles = []
        const errorMessages = []
        let emailErrorFound = false
        result.forEach((row) => {
          const filename = row.filename.trim()
          const emails = row.emails
          const errorMsg = row.errorMsg
          let fileMatch
          const fileIndex = this.uploadedFiles.findIndex(
            (f) => f.name.trim() === filename
          )

          if (errorMsg && errorMsg.length > 0) {
            errorMessages.push(errorMsg)
          }
          if (fileIndex !== -1) {
            fileMatch = this.uploadedFiles[fileIndex]
          }
          if (!fileMatch) {
            missingFiles.push(filename)
          } else {
            if (emails && (emails.length > 0)) {
              const newParticipant = emails.map((email) => ({
                email,
                role: memberRole.SIGNER
              }))
              this.$set(this.documentParticipants, fileIndex, [...newParticipant])
            } else {
              emailErrorFound = true
              this.$set(this.documentParticipants, fileIndex, [])
            }
          }
        })

        let finalMessage = ''
        const fileProblemFound = (missingFiles.length > 0)
        if (fileProblemFound) {
          const missingFileNames = missingFiles.join(', ')
          finalMessage = this.$t('documentSignatureCustomization.content.createChain.csv.missingFilenames', { filenames: missingFileNames })
        }
        /* if (errorMessages.length > 0) {
          const erroMessagesStr = errorMessages.join(', ')
          console.log('There are errors for rows', erroMessagesStr)
          finalMessage = finalMessage + ' ' + erroMessagesStr
        } */
        if (emailErrorFound) {
          finalMessage = finalMessage + ' ' + this.$t('documentSignatureCustomization.content.createChain.csv.invalidEmailFound')
        }

        if (fileProblemFound) {
          this.useIndividualParticipants = false
        } else {
          this.useIndividualParticipants = true
        }
        if (finalMessage.length > 0) {
          finalMessage = this.$t('documentSignatureCustomization.content.createChain.csv.csvLoadingError') + ' ' + finalMessage
          showToastError(finalMessage)
        }
      } catch (err) {
        console.error('csv general error', err)
        showToastError(this.$t('documentSignatureCustomization.content.createChain.csv.csvGeneralError', { error: err.message }))
      }
    },

    saveSettings() {
      if (this.rememberSettings) {
        const settings = {
          useSigningTrail: this.useSigningTrail,
          attachedOrDetachedSigningTrail: this.attachedOrDetachedSigningTrail,
          setSignaturesMode: this.setSignaturesMode,
          asicMode: this.asicMode,
          chosenSignatureType: this.chosenSignatureType,
          rememberSettings: this.rememberSettings
        }

        localStorage.setItem('UploadAdvancedSettings', JSON.stringify(settings))
      } else {
        const settings = {
          rememberSettings: this.rememberSettings
        }
        localStorage.setItem('UploadAdvancedSettings', JSON.stringify(settings))
      }
    },

    loadSettings() {
      const settings = JSON.parse(localStorage.getItem('UploadAdvancedSettings'))
      if (settings && settings.rememberSettings) {
        this.useSigningTrail = settings.useSigningTrail
        this.attachedOrDetachedSigningTrail = settings.attachedOrDetachedSigningTrail
        this.setSignaturesMode = settings.setSignaturesMode
        this.asicMode = settings.asicMode
        this.chosenSignatureType = settings.chosenSignatureType ? settings.chosenSignatureType : settings.UNORDERED
        this.rememberSettings = settings.rememberSettings
      }
    }
  }

}
</script>

<style scoped>
#upload_document_card {
  height: 90vh;
  display: flex;
  flex-direction: column;
}

#upload_document_card .v-card__text {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

#upload_document_card .v-card__actions {
  flex: 0 0 auto;
}

.v-tab {
  text-transform: none !important;
}

.cursor-pointer { cursor: pointer; }
.chevron { transition: transform .2s ease; }
.chevron--open { transform: rotate(180deg); }

::v-deep .v-slide-group__wrapper {
  margin-left: 16px;
  margin-right: 16px;
}

.icon {
    order: 0;
}

.header-text {
    order: 1;
}
</style>
